import { getValidationMsgs } from '@1bill-app/validators/messages';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { unsubscribeForm } from '../auth/create-account/form';
import { environment } from '@1bill-app/env';
import { ROUTE_LOGIN } from '@1bill-app/constants';

interface UnsubscribeResponseBody {
  /**
   * Whether or not the user's unsubscribe was successful.
   */
  success: boolean;
  /**
   * Message indicating the result of the unsubscribe attempt.
   */
  message: string;
  /**
   * Property to uniquely identify the use cases of success or failure of the unsubscribe attempt.
   */
  id: 'NOT_EXISTING' | 'NOT_SUBSCRIBED' | 'ERROR' | 'SUCCESS';
}

const CHECK_TOKEN = true;

@Component({
  templateUrl: './unsubscribe.page.html',
  styleUrls: ['./unsubscribe.page.scss'],
  selector: 'app-unsubscribe',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnsubscribePage implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private router: Router,
  ) {
    this.unsubForm = unsubscribeForm();
    this.b64token = this.route.snapshot.queryParamMap.get('token');
    if (CHECK_TOKEN && !this.b64token) {
      this.router.navigateByUrl(ROUTE_LOGIN);
    }
  }

  b64token: string;
  unsubForm: FormGroup;
  showValidationError = false;
  loading = false;
  successfulUnsubscribe = false;
  unsubscribedEmail: string;
  errorMessage: string;
  readonly ROUTE_LOGIN = ROUTE_LOGIN;
  readonly emailValidationMsg = getValidationMsgs().email;

  get emailControl() {
    return this.unsubForm?.get('email');
  }

  ngOnInit(): void {
    this.b64token = this.route.snapshot.queryParamMap.get('token');
  }

  onClickUnsubscribe() {
    // Prevent double API calls or calls when already done
    if (this.loading || this.successfulUnsubscribe) {
      return;
    }
    if (!this.emailControl.valid) {
      if (this.emailControl.errors) {
        this.errorMessage = this.emailValidationMsg.find((validator) => validator.type === Object.keys(this.emailControl.errors)[0]).message;
      }
      return;
    }
    // Encode first, so that non-ASCII characters can be supported.
    // See: https://datatracker.ietf.org/doc/html/rfc6532
    const inputEmail = this.emailControl.value;
    const encodedEmail = encodeURIComponent(inputEmail);
    const b64Email = btoa(encodedEmail);
    if (CHECK_TOKEN && b64Email !== this.b64token) {
      this.errorMessage = 'You must enter the email address of your 1bill account.';
      return;
    }
    this.loading = true;
    const getUnsubscribeMessage = (identifier: string) => {
      switch (identifier) {
        case 'ERROR':
          return 'Sorry, something went wrong while unsubscribing. Please try again later.';
        case 'NOT_EXISTING':
          return 'The email you entered does not exist in our system.';
        case 'NOT_SUBSCRIBED':
          return 'You have already been unsubscribed.';
        case 'SUCCESS':
          return null;
      }
    };
    fetch(`${environment.notifyApiUrl}emailSubscription`, {
      method: 'PATCH',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: b64Email }),
    })
      .then(async (response: Response) => {
        const blob = await response.blob();
        const responseBody = JSON.parse(await blob.text()) as UnsubscribeResponseBody;
        
        if (responseBody.success || responseBody.id === 'NOT_SUBSCRIBED') {
          this.successfulUnsubscribe = true;
          this.unsubscribedEmail = inputEmail;
        }
        this.errorMessage = getUnsubscribeMessage(responseBody.id);
      })
      .catch((error) => {
        this.errorMessage = 'Sorry, something went wrong while unsubscribing. Please try again later.';
        console.error(error);
        return error;
      })
      .finally(() => {
        this.loading = false;
        this.cdRef.detectChanges();
      });
  }
}
